import React, { useState, useEffect } from 'react';
import { auth, db } from './firebase';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import NavBar from './components/NavBar';
import HomePage from './pages/Questionupload';  // This is the Questionupload component
import { UserProvider } from './pages/UserContext';
import TheoryDashboard from './pages/TheoryDashboard';
import AuthPages from './pages/AuthPages';
import SamplePaperUpload from './pages/SamplePaperUpload';
import AdminHomePage from './pages/AdminHomePage';
import UserManagement from './pages/UserManagement';
import ExploreDataDashboard from './pages/ExploreDataDashboard';
import MembershipManagement from './pages/MembershipManagement';
import UnregisteredAccessPanel from './pages/UnregisteredAccessPanel';
import AccountDeletionRequestsPage from './pages/AccountDeletionRequestsPage';
import SettingsPage from './pages/SettingsPage';

const App = () => {
  const pathToPage = (path) => {
    switch (path) {
      case '/auth': return 'auth';
      case '/theory': return 'TheoryDashboard';
      case '/sample-papers': return 'SamplePaperUpload';
      case '/admin': return 'AdminHomePage';
      case '/users': return 'UserManagement';
      case '/explore': return 'ExploreDataDashboard';
      case '/unregistered-access': return 'UnregisteredAccessPanel';
      case '/questions': return 'home';  // Map /questions to home which is the Questionupload component
      case '/account-deletion-requests': return 'AccountDeletionRequests';
      case '/settings': return 'SettingsPage'; // Added dedicated path for settings
      case '/': return 'AdminHomePage'; // Default to AdminHomePage
      default: return 'AdminHomePage';
    }
  };

  const initialPage = pathToPage(window.location.pathname);
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);

  // Check if user is admin
  const fetchUserRole = async (userId) => {
    try {
      const userDocRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userDocRef);
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        setUserRole(userData.role || null);
        return userData.role || null;
      } else {
        setUserRole(null);
        return null;
      }
    } catch (error) {
      console.error("Error fetching user role:", error);
      setUserRole(null);
      return null;
    }
  };

  useEffect(() => {
    // Listen for authentication state changes
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      
      if (currentUser) {
        // Fetch user role when user is authenticated
        await fetchUserRole(currentUser.uid);
      } else {
        setUserRole(null);
      }
      
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // Redirect after login based on user role
  useEffect(() => {
    if (user && currentPage === 'auth') {
      setCurrentPage('AdminHomePage');
    }
  }, [user, currentPage]);

  // Update the URL path whenever currentPage changes
  useEffect(() => {
    localStorage.setItem('currentPage', currentPage);
    const pageToPath = {
      auth: '/auth',
      home: '/questions',  // This maps the 'home' state to /questions URL
      AdminHomePage: '/',  // AdminHomePage is the home route
      TheoryDashboard: '/theory',
      SamplePaperUpload: '/sample-papers',
      UserManagement: '/users',
      ExploreDataDashboard: '/explore',
      MembershipManagement: '/membership',
      UnregisteredAccessPanel: '/unregistered-access',
      AccountDeletionRequests: '/account-deletion-requests',
      SettingsPage: '/settings', // Added mapping for settings page
    };
    
    const path = pageToPath[currentPage] || '/';
    window.history.pushState(null, '', path);
  }, [currentPage]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Redirect unauthenticated users to the auth page
  if (!user && currentPage !== 'auth') {
    setCurrentPage('auth');
    return null; // Temporarily render nothing while redirecting
  }

  if (currentPage === 'auth' && !user) {
    return <AuthPages setCurrentPage={setCurrentPage} />;
  }

  // Warning component for non-admin users that blocks access
  const AdminAccessDenied = () => (
    <div className="fixed inset-0 bg-gray-900 bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full">
        <div className="flex items-center justify-center mb-6 text-red-600">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <h2 className="text-2xl font-bold text-center text-gray-800 mb-4">Access Denied</h2>
        <p className="text-gray-600 text-center mb-6">
          You don't have admin privileges required to access this page.
        </p>
        <div className="flex justify-center">
          <button
            onClick={() => auth.signOut()}
            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
          >
            Logout
          </button>
        </div>
      </div>
    </div>
  );

  // Check if current page is admin-only
  const isAdminPage = [
    'AdminHomePage', 
    'UserManagement', 
    'MembershipManagement',
    'AccountDeletionRequests',
    'SettingsPage' // Added SettingsPage to admin-only pages
  ].includes(currentPage);

  // Block access to admin pages for non-admin users
  if (isAdminPage && userRole !== 'admin') {
    return <AdminAccessDenied />;
  }

  return (
    <UserProvider>
      <div className="min-h-screen bg-gray-50">
        <NavBar user={user} userRole={userRole} setCurrentPage={setCurrentPage} />
        <main className="max-w-7xl mx-auto px-4 pt-20 pb-6">
          {currentPage === 'home' && (
            <HomePage user={user} setCurrentPage={setCurrentPage} setSelectedUser={setSelectedUser} />
          )}
          {currentPage === 'TheoryDashboard' && (
            <TheoryDashboard user={user} setCurrentPage={setCurrentPage} setSelectedUser={setSelectedUser} />
          )}
          {currentPage === 'SamplePaperUpload' && (
            <SamplePaperUpload user={user} setCurrentPage={setCurrentPage} />
          )}
          {currentPage === 'AdminHomePage' && (
            <AdminHomePage user={user} setCurrentPage={setCurrentPage} />
          )}
          {currentPage === 'UserManagement' && (
            <UserManagement user={user} setCurrentPage={setCurrentPage} />
          )}
          {currentPage === 'ExploreDataDashboard' && (
            <ExploreDataDashboard user={user} setCurrentPage={setCurrentPage} />
          )}
          {currentPage === 'MembershipManagement' && (
            <MembershipManagement user={user} setCurrentPage={setCurrentPage} setSelectedUser={setSelectedUser} />
          )}
          {currentPage === 'UnregisteredAccessPanel' && (
            <UnregisteredAccessPanel user={user} setCurrentPage={setCurrentPage} setSelectedUser={setSelectedUser} />
          )}
          {currentPage === 'AccountDeletionRequests' && (
            <AccountDeletionRequestsPage setCurrentPage={setCurrentPage} />
          )}
          {currentPage === 'SettingsPage' && (
            <SettingsPage user={user} setCurrentPage={setCurrentPage} />
          )}
        </main>
      </div>
    </UserProvider>
  );
};

export default App;